import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Key, 
  Mail, 
  Shield,
  Eye,
  EyeOff,
  Save,
  Globe,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  fr: { collaborateur: 'Collaborateur', manager: 'Manager', directeur: 'Directeur' },
  en: { collaborateur: 'Collaborator', manager: 'Manager', directeur: 'Director' },
  de: { collaborateur: 'Mitarbeiter', manager: 'Manager', directeur: 'Direktor' }
};

const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' }
];

export default function Profile() {
  const { user, updateUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingLang, setSavingLang] = useState(false);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChangePassword = async () => {
    if (!passwordData.new_password) {
      toast.error(t('error'));
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('passwordMismatch'));
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error(t('passwordTooShort') || 'Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        new_password: passwordData.new_password
      });
      toast.success(t('passwordChanged'));
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeLanguage = async (newLang) => {
    setSavingLang(true);
    try {
      await axios.put(`${API}/auth/change-language`, { language: newLang });
      setLanguage(newLang);
      if (updateUser) {
        updateUser({ ...user, default_language: newLang });
      }
      toast.success(t('languageChanged') || 'Langue modifiée');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSavingLang(false);
    }
  };

  if (!user) {
    return null;
  }

  const roleLabels = ROLE_LABELS[language] || ROLE_LABELS.fr;
  const currentLang = user.default_language || language || 'fr';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <User className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl">{t('myProfile').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">{user.email}</p>
        </div>
      </div>

      {/* User Info Card */}
      <div className="border border-border rounded-lg p-6 bg-card space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {t('userInfo') || 'Informations'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{t('firstName')}</p>
            <p className="font-medium">{user.first_name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{t('lastName')}</p>
            <p className="font-medium">{user.last_name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" /> {t('email')}
            </p>
            <p className="font-medium">{user.email}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{t('role')}</p>
            <Badge className="bg-primary text-white">
              {roleLabels[user.role] || user.role}
            </Badge>
          </div>
          {user.color && (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('userColor')}</p>
              <div 
                className="w-8 h-8 rounded-full border-2 border-white shadow"
                style={{ backgroundColor: user.color }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Language Selection Card */}
      <div className="border border-border rounded-lg p-6 bg-card space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Globe className="h-5 w-5" />
          {t('chooseLanguage') || 'Choisir ma langue'}
        </h2>
        
        <div className="flex flex-wrap gap-3">
          {LANGUAGE_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleChangeLanguage(lang.code)}
              disabled={savingLang}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                currentLang === lang.code 
                  ? 'border-primary bg-primary/10 text-primary' 
                  : 'border-border hover:border-primary/50 hover:bg-muted'
              }`}
              data-testid={`lang-${lang.code}-btn`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="font-medium">{lang.label}</span>
              {currentLang === lang.code && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          {t('languageNote') || 'La langue choisie sera utilisée pour l\'interface et les exports PDF.'}
        </p>
      </div>

      {/* Change Password Card */}
      <div className="border border-border rounded-lg p-6 bg-card space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Key className="h-5 w-5" />
          {t('changeOwnPassword')}
        </h2>
        
        <div className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>{t('newPassword')}</Label>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{t('passwordMinLength') || 'Minimum 6 caractères'}</p>
          </div>

          <div className="space-y-2">
            <Label>{t('confirmPassword')}</Label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={saving || !passwordData.new_password || !passwordData.confirm_password}
            data-testid="save-password-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
